import { useEffect, useRef } from 'react';

import { Link, NextLink, Text } from '@/atoms';

import { useIntersectionObserver } from '@/components/hooks';
import { gtmPush, normalizeAlgoliaItems, normalizeItems } from '@/lib/utils';
import styles from './ProductBlock.module.css';

import type { FunctionComponent } from 'react';
import type { TypeProductBlockProps } from './types';

/**
 * ProductBlock
 */
export const ProductBlock: FunctionComponent<TypeProductBlockProps> = ({
  className,
  title,
  description,
  link,
  children,
  itemListId,
  itemListName,
  itemIndex,
  productos,
}: TypeProductBlockProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const entry = useIntersectionObserver(ref, { freezeOnceVisible: true });
  const isVisible = !!entry?.isIntersecting;

  useEffect(() => {
    if (isVisible && productos) {
      if (productos?.at(0)?.hasOwnProperty('_highlightResult')) {
        gtmPush({
          event: 'view_item_list',
          ecommerce: {
            items: normalizeAlgoliaItems(
              productos
                ?.filter(Boolean)
                ?.filter((item) => item?.__typename !== 'Banner'),
              itemListId,
              itemListName,
            ),
          },
        });
      } else {
        gtmPush({
          event: 'view_item_list',
          ecommerce: {
            items: normalizeItems(
              productos
                ?.filter(Boolean)
                ?.filter((item) => item?.__typename !== 'Banner'),
              itemListId,
              itemListName,
              itemIndex,
            ),
          },
        });
      }
    }
  }, [isVisible]);

  return (
    <div {...(className && { className })} ref={ref}>
      <div className={styles['c-product-block__header']}>
        {(title || link) && (
          <div className="flex flex-row items-center justify-between gap-8 md:gap-12">
            {title && link ? (
              <h2>
                <NextLink href={link.href}>
                  <a>
                    <Text className={styles['c-product-block__title']}>
                      {title}
                    </Text>
                  </a>
                </NextLink>
              </h2>
            ) : (
              <Text htmlTag="h2" className={styles['c-product-block__title']}>
                {title}
              </Text>
            )}

            {link && (
              <NextLink href={link.href} passHref>
                <Link
                  size={link.size}
                  type={link.style}
                  className={styles['c-product-block__link']}
                >
                  {link.label}
                </Link>
              </NextLink>
            )}
          </div>
        )}

        {description && (
          <div className="u-body u-body--xl pt-2 text-typo-secondary">
            {description}
          </div>
        )}
      </div>

      {children}
    </div>
  );
};
